import React, { createContext, useContext, useState, useCallback } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [taxRate] = useState(0.08); // 8% tax rate

  const addItem = useCallback((product, quantity = 1) => {
    setItems(prevItems => {
      const existingItem = prevItems.find(item => item.product_id === product.id);
      
      if (existingItem) {
        return prevItems.map(item =>
          item.product_id === product.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      } else {
        return [...prevItems, {
          product_id: product.id,
          quantity,
          unit_price: product.price,
          product: product
        }];
      }
    });
  }, []);

  const removeItem = useCallback((productId) => {
    setItems(prevItems => prevItems.filter(item => item.product_id !== productId));
  }, []);

  const updateQuantity = useCallback((productId, quantity) => {
    if (quantity <= 0) {
      removeItem(productId);
      return;
    }
    
    setItems(prevItems =>
      prevItems.map(item =>
        item.product_id === productId
          ? { ...item, quantity }
          : item
      )
    );
  }, [removeItem]);

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  const getSubtotal = useCallback(() => {
    return items.reduce((total, item) => total + (item.quantity * item.unit_price), 0);
  }, [items]);

  const getTaxAmount = useCallback(() => {
    return getSubtotal() * taxRate;
  }, [getSubtotal, taxRate]);

  const getTotal = useCallback(() => {
    return getSubtotal() + getTaxAmount();
  }, [getSubtotal, getTaxAmount]);

  const getItemCount = useCallback(() => {
    return items.reduce((total, item) => total + item.quantity, 0);
  }, [items]);

  const value = {
    items,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    subtotal: getSubtotal(),
    taxAmount: getTaxAmount(),
    total: getTotal(),
    itemCount: getItemCount(),
    taxRate,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};
